<template>
    <div id="spacer">
        <div class="row">
            <div class="col">
                <div class="alert alert-warning alert-dismissible" role="alert" v-if="periodShort">
            <button type="button" class="close" aria-label="Close" @click="periodShort = false"><span aria-hidden="true">×</span></button>
            <strong>{{ $t('components.coreDatePicker.invalidPeriod.title') }}</strong><br>
            <i18n path="components.coreDatePicker.invalidPeriod.message" tag="span">
                <template v-slot:minimumNights>
                    <span>{{ minimumNights }}</span>
                </template>
            </i18n>
        </div>
            </div>
        </div>
        <div class="row">
            <div class="col d-flex justify-content-center">
                <vc-date-picker 
                    v-model="selectedDate" 
                    v-bind="$attrs"
                    :attributes="computeAttributes"             
                    :disabled-dates="disabledDates"
                    :min-date="new Date()" 
                    mode="range"
                    :step="1"
                    is-inline
                />
            </div>
        </div>
        
    </div>
</template>

<script>
export default {
    name: "CoreDatePicker",
    props: {
        value: {
            type: Object,
            required: false,
            default: () => {}
        },
        bookings: {
            type: Array,
            required: false,
            default: () => [] // Use an empty array
        },
    },
    watch: {
        selectedDate: function(newVal, oldVal) {
            if (!this.isUndefined(newVal.start) && !this.isUndefined(newVal.end)) {
                const date1 = new Date(newVal.start);
                const date2 = new Date(newVal.end);
                const diffTime = Math.abs(date2 - date1);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; 

                if (diffDays < this.minimumDays) {
                    // reset the dates
                    this.selectedDate = {
                        start: undefined,
                        end: undefined
                    }
                    this.periodShort = true;
                } else {
                    this.periodShort = false;
                }
            }
        }
    },
    computed: {
        rawDates: function() {
            return this.bookings.map(booking => {
                let startDate = new Date(booking.startDate);
                let endDate = new Date(booking.endDate);
                
                return {
                    start: startDate, 
                    end: endDate
                }
            }).filter(el => this.$moment(el.end).isAfter(this.$moment().startOf('month')))
            .sort((a, b) => {
                let isAfter = this.$moment(a.end).isAfter(this.$moment(b.start));

                return isAfter ? 1 : -1;
            });
        },
        computeDates: function() {
            // We copy the periods into a new array, and make mods in place
            let dates = [];
            for (let i = 0; i < this.rawDates.length; i++) {
                dates.push(this.rawDates[i]);
            }

            // This covers a special case, where the number of bookings is 1
            // In this case, the loop will never run so we need to enforce the shrinking.
            // Since there is no previous or next period, we already know that we can
            // safely shrink it
            if (dates.length === 1) {
                // If there is only one date, we need to simply shrink it
                let current = dates[0];
                
                let currentStartDate = this.castDate(current.start)
                let currentEndDate = this.castDate(current.end)

                // Shorten the period by one day, in both directions
                currentStartDate = this.addDays(currentStartDate, 1);
                currentEndDate = this.addDays(currentEndDate, -1);


                let currentDates = {
                    start: currentStartDate,
                    end: currentEndDate
                }

                dates[0] = currentDates;

                return dates;
            }

            // This happens only when the number of bookings available is > 1. 
            // In this case, we need to adapt the different dates to avoid confusion when picking
            // the travel period
            for (let i = 0; i < dates.length - 1; i++) {
                let current = dates[i];
                let next = dates[i + 1];

                let currentStartDate = this.castDate(current.start)
                let currentEndDate = this.castDate(current.end)

                let nextStartDate = this.castDate(next.start);
                let nextEndDate = this.castDate(next.end);

                
                if (i === 0) {
                    // If first period, shorten by one from the head
                    currentStartDate = this.addDays(currentStartDate, 1);
                }

                if (i + 1 === dates.length - 1) {
                    // If last one, shorten by one from the tail
                    nextEndDate = this.addDays(nextEndDate, -1);
                }

                // In any case, analyse the space between the tail of period 1, and the head of period 2
                // If the difference is <= 2, then make them match. 
                // In the other cases, increase the gap so we have enough space
                // for a user to select it 
                let differenceInDays = this.dateDiffInDays(currentEndDate, nextStartDate);
                if (differenceInDays <= 2 && differenceInDays > 0) {
                    // Make them match
                    nextStartDate = this.addDays(nextStartDate, -1 * differenceInDays);
                } else if (differenceInDays !== 0) { // Handle any case except for when the diff is 0
                    // Shorten/push them
                    currentEndDate = this.addDays(currentEndDate, -1);
                    nextStartDate = this.addDays(nextStartDate, 1);
                }

                // Set them in the dates array
                let currentDates = {
                    start: currentStartDate,
                    end: currentEndDate
                }

                let nextDates = {
                    start: nextStartDate,
                    end: nextEndDate
                }

                dates[i] = currentDates;
                dates[i + 1] = nextDates;
            }
            return dates;
        },
        computeAttributes: function () {
            return [{
                key: 'bookings',
                dates: this.computeDates.filter(d => d.start !== undefined && d.start !== null),
                highlight: {
                    color: 'red',
                }
            }]
        },
        disabledDates: function () {
            return this.computeDates.map(date => {
                return {
                    start: new Date(date.start),
                    end: new Date(date.end)
                }
            });
        },
        selectedDate: {
            get() {
                console.log("[CoreDatePicker] - Getting value")
                return this.value;
            },
            set(val) {
                console.log("[CoreDatePicker] - Setting new value")
                this.$emit('input', val);
            }
        },
        minimumNights: function() {
            return this.minimumDays - 1;
        }
        
    },
    methods: {
        dateIsBetween: function(ref, lower, upper) {
            ref = this.castDate(ref);
            lower = this.castDate(lower);
            upper = this.castDate(upper);

            return ref.getTime() >= lower.getTime() && ref.getTime() <= upper.getTime();
        },
        addDays: function(date, days) {
            return this.$moment(date).add(days, 'days').toDate();
        },
        dateDiffInDays: function(a, b) {
            var start = this.$moment(a).startOf('day');
            var end = this.$moment(b).startOf('day');

            //Difference in number of days
            let diffDays = Math.ceil(this.$moment.duration(end.diff(start)).asDays());

            return diffDays;
        }
    },
    created() {
        if (!this.isUndefined(this.selectedDate)) {
            // This prevents invalid dates from being used when booking. If the user goes from one listing to another, and the 
            // selected period is already booked, this incalidates that period. Otherwise, the user can select a period that was
            // already reserved
            for (let i = 0; i < this.computeDates.length; i++) {
                let { start, end } = this.computeDates[i];
                let res = [
                    this.selectedDate.start, 
                    this.selectedDate.end
                ]
                .filter(el => el !== undefined)
                .some(el => this.dateIsBetween(el, start, end));

                if (res) {
                    // Invalidate it
                    this.selectedDate = {
                        start: undefined,
                        end: undefined
                    }
                }
            }

            // Cast the dates from string to date
            if (!this.isUndefined(this.selectedDate.start)) {
                this.selectedDate.start = this.castDate(this.selectedDate.start)
            }

            if (!this.isUndefined(this.selectedDate.end)) {
                this.selectedDate.end = this.castDate(this.selectedDate.end)
            }
        }
    },
    data() {
        return {
            periodShort: false,
            minimumDays: 4
        }
    }
}
</script>

<style lang="scss" scoped>
</style>